import React from "react";
import { useNavigate } from "react-router-dom";
import { Users, DoorOpen, Fuel, Settings2, Snowflake, Star, CheckCircle2, GitCompare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { WhatsAppContact } from "@/components/WhatsAppContact";

export const VehicleCard = ({ vehicle, dates = null }) => {
  const detailUrl = dates ? `/car/${vehicle.id}?start=${dates.start}&end=${dates.end}` : `/car/${vehicle.id}`;
  const navigate = useNavigate();
  const { compare, toggleCompare } = useAuth();
  const inCompare = compare.find((v) => v.id === vehicle.id);

  const specs = [
    { icon: Users, label: `${vehicle.seats} places` },
    { icon: DoorOpen, label: `${vehicle.doors} portes` },
    { icon: Settings2, label: vehicle.transmission },
    { icon: Fuel, label: vehicle.fuel },
  ];

  return (
    <div
      data-testid={`vehicle-card-${vehicle.id}`}
      className="lr-card-hover group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
        <img
          src={vehicle.image_url}
          alt={`${vehicle.brand} ${vehicle.model}`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute top-3 left-3 rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
          {vehicle.category}
        </span>
        {vehicle.available ? (
          <span className="absolute top-3 right-3 flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white" style={{ background: "#006233" }}>
            <CheckCircle2 className="h-3 w-3" /> Disponible
          </span>
        ) : (
          <span className="absolute top-3 right-3 rounded-full bg-slate-500 px-2.5 py-1 text-[11px] font-semibold text-white">Indisponible</span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900 leading-tight">
              {vehicle.brand} {vehicle.model}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">{vehicle.year} · {vehicle.city}</p>
          </div>
          {vehicle.agency && (
            <span className="flex items-center gap-0.5 text-xs font-semibold text-amber-600">
              <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> {vehicle.agency.rating}
            </span>
          )}
        </div>

        {vehicle.agency && (
          <div className="mt-2 flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs text-slate-600">
              <CheckCircle2 className="h-3.5 w-3.5" style={{ color: "#006233" }} />
              <span className="text-slate-500">Loueur vérifié · contact direct</span>
            </span>
            <WhatsAppContact vehicle={vehicle} dates={dates} />
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-slate-600">
          {specs.map((s, i) => (
            <span key={i} className="flex items-center gap-1.5">
              <s.icon className="h-3.5 w-3.5 text-slate-400" /> {s.label}
            </span>
          ))}
          {vehicle.air_conditioning && (
            <span className="flex items-center gap-1.5 col-span-2">
              <Snowflake className="h-3.5 w-3.5 text-slate-400" /> Climatisation
            </span>
          )}
        </div>

        <div className="mt-5 flex items-end justify-between border-t border-slate-100 pt-4">
          <div>
            <span className="font-display text-2xl font-extrabold text-slate-900">{vehicle.price_per_day}</span>
            <span className="text-sm font-medium text-slate-500"> MAD / jour</span>
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <Button
            variant={inCompare ? "default" : "outline"}
            size="sm"
            data-testid={`vehicle-card-compare-btn-${vehicle.id}`}
            onClick={() => toggleCompare(vehicle)}
            className={inCompare ? "text-white" : ""}
            style={inCompare ? { background: "#0B132B" } : {}}
          >
            <GitCompare className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            data-testid={`vehicle-card-details-btn-${vehicle.id}`}
            onClick={() => navigate(detailUrl)}
            className="flex-1 text-white"
            style={{ background: "#006233" }}
          >
            Voir & réserver
          </Button>
        </div>
      </div>
    </div>
  );
};
